/**
 * FIFO Intelligence Workspace — Sprint 4 (DEC-046 · DEC-048).
 *
 * Answers the fourth operational question the platform is organised around:
 *
 *     "What does our inventory actually cost?"
 *
 * Sibling to Inventory (Q1), Purchasing (Q2), and Pricing (Q3). Together
 * they complete the four-domain operational information architecture.
 *
 * Sprint 4 · V1 (PO-approved):
 *   • Cost Snapshot   — landed cost · next-out cost · portfolio value.
 *   • Layers          — per-product open cost layers, oldest first.
 *   • Receipts        — inbound purchases with add-on breakdown.
 *   • Consumption     — simulated FIFO drawdown history.
 *   • Opening Wizard  — seed day-one balances (once per product).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Stack, TrendUp, Truck, Drop, Sparkle, ArrowsClockwise, X, Plus,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const fifoApi = {
  snapshot:        ()               => api.get("/fifo/snapshot"),
  layers:          (p)              => api.get(`/fifo/layers/${p}`),
  receipts:        (params)         => api.get("/fifo/receipts", { params }),
  postReceipt:     (payload)        => api.post("/fifo/receipts", payload),
  consumption:     (params)         => api.get("/fifo/consumption", { params }),
  simulate:        (payload)        => api.post("/fifo/consumption/simulate", payload),
  openingState:    ()               => api.get("/fifo/opening-inventory"),
  postOpening:     (payload)        => api.post("/fifo/opening-inventory", payload),
};

const PRODUCTS = ["regular", "premium", "clear_diesel", "dyed_diesel", "def"];
const PRODUCT_LABELS = {
  regular:      "Regular",
  premium:      "Premium",
  clear_diesel: "Clear Diesel",
  dyed_diesel:  "Dyed Diesel",
  def:          "DEF",
};

function fmtDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
function fmtRelDays(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 1) return "today";
  if (days === 1) return "1 day ago";
  return `${days} days ago`;
}
function money(v, places = 4) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return `$${n.toFixed(places)}`;
}
function litres(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!isFinite(n)) return "—";
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })} L`;
}

/* ------------------------------------------------------------------
   Cost Snapshot
   ------------------------------------------------------------------ */
function SnapshotTab({ refreshKey, onOpenWizard, canWrite }) {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await fifoApi.snapshot(); setSnap(data); }
    catch (e) { toast.error("Failed to load cost snapshot"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const missing = useMemo(
    () => (snap?.items || []).filter((it) => !it.has_opening_inventory && it.open_layer_count === 0),
    [snap]
  );

  if (loading && !snap) return <p className="text-sm text-slate-500">Loading…</p>;
  if (!snap) return null;

  return (
    <div className="space-y-4" data-testid="fifo-snapshot-tab">
      {missing.length > 0 && canWrite ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
             data-testid="fifo-opening-banner">
          <Sparkle size={16} weight="fill" className="text-amber-600 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-amber-900 font-medium">
              {missing.length} product{missing.length === 1 ? "" : "s"} still need opening inventory.
            </p>
            <p className="text-xs text-amber-800 mt-0.5">
              The engine cannot report a landed cost until each product has an opening balance or a first receipt.
            </p>
          </div>
          <button type="button" onClick={onOpenWizard}
                  data-testid="fifo-open-wizard-cta"
                  className="text-xs font-semibold px-3 py-1.5 rounded bg-amber-600 text-white hover:bg-amber-700">
            Open Wizard
          </button>
        </div>
      ) : null}

      <div className="flex items-baseline gap-4 flex-wrap">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">FIFO Fuel Inventory Value</div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Gasoline and diesel products tracked by the FIFO engine. Other inventory categories are excluded.
          </div>
        </div>
        <div className="text-lg font-semibold text-slate-900 tabular-nums" data-testid="fifo-portfolio-value">
          ${snap.portfolio_value
              ? Number(snap.portfolio_value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              : "0.00"} CAD
        </div>
        <div className="ml-auto text-[11px] text-slate-500">
          Generated {fmtDT(snap.generated_at)}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {snap.items.map((it) => (
          <div key={it.product_code}
               data-testid={`fifo-snapshot-card-${it.product_code}`}
               className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900">{it.product_label}</div>
                <div className="text-[11px] text-slate-500">{it.product_code}</div>
              </div>
              <div className={`text-[11px] px-2 py-0.5 rounded-full border ${
                  it.open_layer_count > 0 ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                  : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                {it.open_layer_count} layer{it.open_layer_count === 1 ? "" : "s"}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="rounded bg-slate-50 p-2">
                <div className="text-slate-500">Weighted avg landed</div>
                <div className="font-semibold text-slate-900 text-sm tabular-nums"
                     data-testid={`fifo-wavg-${it.product_code}`}>
                  {money(it.weighted_avg_unit_cost)}
                </div>
              </div>
              <div className="rounded bg-slate-50 p-2">
                <div className="text-slate-500">Next litre out</div>
                <div className="font-semibold text-slate-900 text-sm tabular-nums"
                     data-testid={`fifo-nextout-${it.product_code}`}>
                  {money(it.next_out_unit_cost)}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs">
              <div className="text-slate-600">
                <span className="text-slate-500">On hand · </span>
                <span className="font-medium tabular-nums" data-testid={`fifo-qty-${it.product_code}`}>
                  {litres(it.total_quantity)}
                </span>
              </div>
              <div className="text-slate-600">
                <span className="text-slate-500">Value · </span>
                <span className="font-medium tabular-nums" data-testid={`fifo-value-${it.product_code}`}>
                  {it.total_inventory_value ? `$${Number(it.total_inventory_value).toFixed(2)}` : "—"}
                </span>
              </div>
            </div>

            <div className="text-[11px] text-slate-500">
              {it.oldest_layer_received_at
                ? <>Oldest layer <b>{fmtRelDays(it.oldest_layer_received_at)}</b></>
                : <span className="italic">No layers yet</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
   Layers Tab
   ------------------------------------------------------------------ */
function LayersTab({ refreshKey }) {
  const [product, setProduct] = useState("regular");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await fifoApi.layers(product); setItems(data.items || []); }
    catch (e) { toast.error("Failed to load layers"); }
    finally { setLoading(false); }
  }, [product]);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="space-y-3" data-testid="fifo-layers-tab">
      <div className="flex items-center gap-3">
        <label className="text-xs text-slate-600">Product</label>
        <select value={product} onChange={(e) => setProduct(e.target.value)}
                data-testid="fifo-layers-product"
                className="text-xs rounded border border-slate-300 px-2 py-1">
          {PRODUCTS.map((p) => <option key={p} value={p}>{PRODUCT_LABELS[p]}</option>)}
        </select>
        <span className="ml-auto text-xs text-slate-500" data-testid="fifo-layers-count">
          {items.length} open layer{items.length === 1 ? "" : "s"}
        </span>
      </div>
      {loading ? <p className="text-sm text-slate-500">Loading…</p> : items.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500"
             data-testid="fifo-layers-empty">
          No open layers for {PRODUCT_LABELS[product]}. Seed opening inventory or post a receipt to begin.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Age</th>
                <th className="px-3 py-2 text-left font-medium">Received</th>
                <th className="px-3 py-2 text-left font-medium">Source</th>
                <th className="px-3 py-2 text-right font-medium">Original</th>
                <th className="px-3 py-2 text-right font-medium">Remaining</th>
                <th className="px-3 py-2 text-right font-medium">Landed unit cost</th>
                <th className="px-3 py-2 text-right font-medium">Remaining value</th>
              </tr>
            </thead>
            <tbody>
              {items.map((l, idx) => {
                const remaining = Number(l.quantity_remaining) || 0;
                const original  = Number(l.original_quantity) || 0;
                const drawn = original > 0 ? Math.round(((original - remaining) / original) * 100) : 0;
                const cost = Number(l.landed_unit_cost) || 0;
                return (
                  <tr key={l.id} className="border-t border-slate-100 hover:bg-slate-50"
                      data-testid={`fifo-layer-row-${l.id}`}>
                    <td className="px-3 py-2">
                      <span className={`inline-block w-2 h-2 rounded-full mr-2 ${idx === 0 ? "bg-amber-500" : "bg-slate-300"}`} />
                      <span className="text-xs text-slate-600">{fmtRelDays(l.received_at) || "—"}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">{fmtDT(l.received_at)}</td>
                    <td className="px-3 py-2">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${
                        l.source === "opening" ? "bg-sky-50 text-sky-800 border-sky-200"
                        : "bg-slate-50 text-slate-700 border-slate-200"}`}>
                        {l.source}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{litres(l.original_quantity)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">
                      {litres(l.quantity_remaining)}
                      {drawn > 0 ? <span className="ml-1 text-[10px] text-slate-500">({drawn}% drawn)</span> : null}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-800">{money(l.landed_unit_cost)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                      ${(remaining * cost).toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------
   Post-Receipt Dialog
   ------------------------------------------------------------------ */
function PostReceiptDialog({ open, onClose, onDone }) {
  const [product, setProduct] = useState("regular");
  const [quantity, setQuantity] = useState("");
  const [cost, setCost] = useState("");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [addOns, setAddOns] = useState([]);   // {name, amount, basis}
  const [saving, setSaving] = useState(false);

  const landedPreview = useMemo(() => {
    const q = parseFloat(quantity);
    const base = parseFloat(cost);
    if (!isFinite(base)) return null;
    let perLitre = base;
    for (const a of addOns) {
      const amt = parseFloat(a.amount);
      if (!isFinite(amt)) continue;
      if (a.basis === "per_litre") perLitre += amt;
      else if (isFinite(q) && q > 0) perLitre += amt / q;
    }
    return perLitre;
  }, [quantity, cost, addOns]);

  const reset = () => {
    setProduct("regular"); setQuantity(""); setCost("");
    setReference(""); setNotes(""); setAddOns([]);
  };

  const submit = async () => {
    if (!quantity || !cost) { toast.error("Quantity and supplier unit cost are required"); return; }
    setSaving(true);
    try {
      const payload = {
        product_code:       product,
        quantity:           quantity,
        supplier_unit_cost: cost,
        source_reference:   reference || null,
        notes:              notes || null,
        add_ons: addOns
          .filter((a) => a.name && a.amount)
          .map((a) => ({ name: a.name, amount: a.amount, basis: a.basis })),
      };
      const { data } = await fifoApi.postReceipt(payload);
      toast.success(`Receipt posted · landed ${money(data.receipt.landed_unit_cost)}/L`);
      reset(); onDone(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to post receipt");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
         data-testid="fifo-receipt-dialog">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <h3 className="text-base font-semibold text-slate-900">Manual Receipt <span className="text-xs font-normal text-slate-500">· Advanced fallback</span></h3>
          <button onClick={onClose} data-testid="fifo-receipt-close" className="text-slate-500 hover:text-slate-900"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="fifo-receipt-fallback-notice">
            <b>Use only when the AI workflow can't be used.</b> For everyday supplier invoices, drop the PDF in <b>Receiving</b> — the parser extracts every field, matches it against your Price Notification, and creates the FIFO layer + inventory increment automatically.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600">Product</label>
              <select value={product} onChange={(e) => setProduct(e.target.value)}
                      data-testid="fifo-receipt-product"
                      className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5">
                {PRODUCTS.map((p) => <option key={p} value={p}>{PRODUCT_LABELS[p]}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600">Quantity (litres)</label>
              <input value={quantity} onChange={(e) => setQuantity(e.target.value)}
                     data-testid="fifo-receipt-quantity"
                     type="number" step="0.01" min="0"
                     className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5 tabular-nums" />
            </div>
            <div>
              <label className="text-xs text-slate-600">Supplier unit cost ($/L)</label>
              <input value={cost} onChange={(e) => setCost(e.target.value)}
                     data-testid="fifo-receipt-cost"
                     type="number" step="0.0001" min="0"
                     className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5 tabular-nums" />
            </div>
            <div>
              <label className="text-xs text-slate-600">Source reference (PO / invoice)</label>
              <input value={reference} onChange={(e) => setReference(e.target.value)}
                     data-testid="fifo-receipt-reference"
                     className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs text-slate-600 font-medium">Landed-cost add-ons</label>
              <button type="button" data-testid="fifo-receipt-addon-add"
                      onClick={() => setAddOns((a) => [...a, { name: "", amount: "", basis: "per_litre" }])}
                      className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-1">
                <Plus size={12} /> Add
              </button>
            </div>
            {addOns.length === 0 ? (
              <p className="text-[11px] text-slate-500 mt-1">No add-ons. Landed cost equals supplier unit cost.</p>
            ) : (
              <div className="mt-2 space-y-2">
                {addOns.map((a, i) => (
                  <div key={i} className="grid grid-cols-[1fr_120px_130px_28px] gap-2 items-center">
                    <input value={a.name} onChange={(e) => setAddOns((arr) => arr.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                           data-testid={`fifo-receipt-addon-name-${i}`}
                           placeholder="e.g. Freight" className="text-sm rounded border border-slate-300 px-2 py-1" />
                    <input value={a.amount} type="number" step="0.0001" min="0"
                           onChange={(e) => setAddOns((arr) => arr.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))}
                           data-testid={`fifo-receipt-addon-amount-${i}`}
                           placeholder="Amount" className="text-sm rounded border border-slate-300 px-2 py-1 tabular-nums" />
                    <select value={a.basis}
                            onChange={(e) => setAddOns((arr) => arr.map((x, j) => j === i ? { ...x, basis: e.target.value } : x))}
                            data-testid={`fifo-receipt-addon-basis-${i}`}
                            className="text-sm rounded border border-slate-300 px-2 py-1">
                      <option value="per_litre">per litre</option>
                      <option value="total">total $</option>
                    </select>
                    <button type="button" onClick={() => setAddOns((arr) => arr.filter((_, j) => j !== i))}
                            data-testid={`fifo-receipt-addon-remove-${i}`}
                            className="text-slate-400 hover:text-rose-600"><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs text-slate-600">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                      data-testid="fifo-receipt-notes"
                      rows={2}
                      className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5" />
          </div>

          <div className="rounded-md bg-slate-900 text-white p-3 flex items-center justify-between">
            <div className="text-xs text-slate-300">Landed unit cost preview</div>
            <div className="text-lg font-semibold tabular-nums" data-testid="fifo-receipt-landed-preview">
              {landedPreview !== null && isFinite(landedPreview) ? `$${landedPreview.toFixed(4)} / L` : "—"}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button onClick={onClose} data-testid="fifo-receipt-cancel"
                  className="text-sm px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button onClick={submit} disabled={saving} data-testid="fifo-receipt-submit"
                  className="text-sm px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
            {saving ? "Posting…" : "Post Manual Receipt"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
   Receipts Tab
   ------------------------------------------------------------------ */
function ReceiptsTab({ refreshKey, canWrite, onRefresh }) {
  const [items, setItems] = useState([]);
  const [product, setProduct] = useState("");
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = product ? { product_code: product } : {};
      const { data } = await fifoApi.receipts(params);
      setItems(data.items || []);
    } catch (e) { toast.error("Failed to load receipts"); }
    finally { setLoading(false); }
  }, [product]);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="space-y-3" data-testid="fifo-receipts-tab">
      <div className="flex items-center gap-3">
        <label className="text-xs text-slate-600">Product</label>
        <select value={product} onChange={(e) => setProduct(e.target.value)}
                data-testid="fifo-receipts-product"
                className="text-xs rounded border border-slate-300 px-2 py-1">
          <option value="">All products</option>
          {PRODUCTS.map((p) => <option key={p} value={p}>{PRODUCT_LABELS[p]}</option>)}
        </select>
        <span className="ml-auto text-xs text-slate-500" data-testid="fifo-receipts-count">
          {items.length} receipt{items.length === 1 ? "" : "s"}
        </span>
        {canWrite ? (
          <button onClick={() => setDialog(true)} data-testid="fifo-receipts-manual"
                  title="Fallback for AI extraction failures, historical corrections, or emergency deliveries. For everyday receiving, use the Receiving workspace."
                  className="text-xs font-medium px-3 py-1.5 rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1">
            <Plus size={12} /> Manual Receipt <span className="text-[10px] text-slate-500 font-normal ml-1">Advanced</span>
          </button>
        ) : null}
      </div>

      {loading ? <p className="text-sm text-slate-500">Loading…</p> : items.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500"
             data-testid="fifo-receipts-empty">
          No receipts posted yet.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Received</th>
                <th className="px-3 py-2 text-left font-medium">Product</th>
                <th className="px-3 py-2 text-right font-medium">Quantity</th>
                <th className="px-3 py-2 text-right font-medium">Supplier / L</th>
                <th className="px-3 py-2 text-right font-medium">Add-ons / L</th>
                <th className="px-3 py-2 text-right font-medium">Landed / L</th>
                <th className="px-3 py-2 text-left font-medium">Reference</th>
                <th className="px-3 py-2 text-left font-medium">Posted by</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`fifo-receipt-row-${r.id}`}>
                  <td className="px-3 py-2 text-xs text-slate-600">{fmtDT(r.received_at)}</td>
                  <td className="px-3 py-2 text-slate-800">{PRODUCT_LABELS[r.product_code] || r.product_code}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">{litres(r.quantity)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">{money(r.supplier_unit_cost)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                    {r.add_ons_total_per_litre && Number(r.add_ons_total_per_litre) > 0
                      ? `+${money(r.add_ons_total_per_litre)}`
                      : "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">{money(r.landed_unit_cost)}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.source_reference || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.posted_by}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <PostReceiptDialog open={dialog} onClose={() => setDialog(false)} onDone={() => { load(); onRefresh(); }} />
    </div>
  );
}

/* ------------------------------------------------------------------
   Consumption Simulation Tab
   ------------------------------------------------------------------ */
function ConsumptionTab({ refreshKey, canWrite, onRefresh }) {
  const [product, setProduct] = useState("regular");
  const [quantity, setQuantity] = useState("");
  const [reason, setReason] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await fifoApi.consumption(); setItems(data.items || []); }
    catch (e) { toast.error("Failed to load consumption history"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const submit = async () => {
    if (!quantity) { toast.error("Quantity required"); return; }
    setSaving(true);
    try {
      const { data } = await fifoApi.simulate({
        product_code: product,
        quantity:     quantity,
        reason:       reason || null,
      });
      toast.success(`Drew ${litres(data.event.quantity)} · wavg ${money(data.event.weighted_avg_unit_cost)}/L`);
      setQuantity(""); setReason("");
      load(); onRefresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string" ? d : d?.message || "Simulation failed";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="fifo-consumption-tab">
      {canWrite ? (
        <div className="rounded-md border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 mb-3">
            <Drop size={16} className="text-slate-500" />
            <h4 className="text-sm font-semibold text-slate-900">Simulate consumption</h4>
            <span className="text-[11px] text-slate-500">
              FIFO drawdown against open layers · until the live sales feed lands.
            </span>
          </div>
          <div className="grid grid-cols-[180px_180px_1fr_120px] gap-2 items-end">
            <div>
              <label className="text-xs text-slate-600">Product</label>
              <select value={product} onChange={(e) => setProduct(e.target.value)}
                      data-testid="fifo-simulate-product"
                      className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5">
                {PRODUCTS.map((p) => <option key={p} value={p}>{PRODUCT_LABELS[p]}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-600">Quantity (L)</label>
              <input value={quantity} onChange={(e) => setQuantity(e.target.value)}
                     data-testid="fifo-simulate-quantity"
                     type="number" step="0.01" min="0"
                     className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5 tabular-nums" />
            </div>
            <div>
              <label className="text-xs text-slate-600">Reason (optional)</label>
              <input value={reason} onChange={(e) => setReason(e.target.value)}
                     data-testid="fifo-simulate-reason"
                     placeholder="e.g. Delivery to customer batch"
                     className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5" />
            </div>
            <button onClick={submit} disabled={saving}
                    data-testid="fifo-simulate-submit"
                    className="h-[34px] rounded bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-50">
              {saving ? "Drawing…" : "Draw"}
            </button>
          </div>
        </div>
      ) : null}

      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-slate-900">History</h4>
          <span className="text-xs text-slate-500" data-testid="fifo-consumption-count">{items.length} event{items.length === 1 ? "" : "s"}</span>
        </div>
        {loading ? <p className="text-sm text-slate-500">Loading…</p> : items.length === 0 ? (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500"
               data-testid="fifo-consumption-empty">
            No consumption events yet.
          </div>
        ) : (
          <ul className="space-y-2">
            {items.map((c) => (
              <li key={c.id} className="rounded border border-slate-200 bg-white p-3"
                  data-testid={`fifo-consumption-row-${c.id}`}>
                <div className="flex items-center gap-3">
                  <TrendUp size={16} className="text-slate-400" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-slate-800">
                      {PRODUCT_LABELS[c.product_code] || c.product_code} · {litres(c.quantity)}
                    </div>
                    <div className="text-xs text-slate-600">
                      Weighted-avg <b>{money(c.weighted_avg_unit_cost)}</b> · Total cost <b>${Number(c.total_cost || 0).toFixed(2)}</b>
                      {" · "}{c.layers_drawn?.length || 0} layer{(c.layers_drawn?.length || 0) === 1 ? "" : "s"} drawn
                      {c.reason ? <span className="ml-1 text-slate-500">— {c.reason}</span> : null}
                    </div>
                  </div>
                  <div className="text-right text-xs text-slate-500">
                    <div>{fmtDT(c.occurred_at)}</div>
                    <div>{c.posted_by}</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
   Opening Inventory Wizard
   ------------------------------------------------------------------ */
function OpeningInventoryWizard({ open, onClose, onDone }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState("");
  const [cost, setCost] = useState("");
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await fifoApi.openingState(); setState(data); }
    catch (e) { toast.error("Failed to load opening state"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  useEffect(() => {
    if (!state) return;
    const firstUnseeded = state.items.find((it) => !it.seeded);
    setSelected(firstUnseeded?.product_code || null);
    setQty(""); setCost(""); setNotes("");
  }, [state]);

  const submit = async () => {
    if (!selected || !qty || !cost) { toast.error("Product, quantity and opening cost required"); return; }
    setSaving(true);
    try {
      await fifoApi.postOpening({
        product_code:      selected,
        quantity:          qty,
        opening_unit_cost: cost,
        notes:             notes || null,
      });
      toast.success(`Opening balance seeded for ${PRODUCT_LABELS[selected]}`);
      setQty(""); setCost(""); setNotes("");
      load();
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to seed opening balance");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
         data-testid="fifo-wizard">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-amber-500" />
            <h3 className="text-base font-semibold text-slate-900">Opening Inventory Wizard</h3>
          </div>
          <button onClick={onClose} data-testid="fifo-wizard-close" className="text-slate-500 hover:text-slate-900"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-slate-600">
            Seed each product with its day-one balance. Once seeded, a product's opening layer is permanent — subsequent
            changes flow through receipts and consumption events.
          </p>

          {loading || !state ? <p className="text-sm text-slate-500">Loading…</p> : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {state.items.map((it) => (
                  <button key={it.product_code}
                          onClick={() => it.seeded ? null : setSelected(it.product_code)}
                          disabled={it.seeded}
                          data-testid={`fifo-wizard-product-${it.product_code}`}
                          className={`text-left rounded border p-3 ${
                            it.seeded ? "bg-emerald-50 border-emerald-200 cursor-default"
                            : selected === it.product_code ? "bg-slate-900 text-white border-slate-900"
                            : "bg-white border-slate-200 hover:border-slate-400"}`}>
                    <div className="text-sm font-semibold">{it.product_label}</div>
                    <div className={`text-[11px] mt-0.5 ${selected === it.product_code && !it.seeded ? "text-slate-300" : "text-slate-500"}`}>
                      {it.seeded
                        ? <>Seeded · {litres(it.opening?.quantity)} @ {money(it.opening?.opening_unit_cost)}</>
                        : "Not yet seeded"}
                    </div>
                  </button>
                ))}
              </div>

              {state.all_seeded ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800"
                     data-testid="fifo-wizard-complete">
                  All products have opening balances. You can close this wizard.
                </div>
              ) : selected ? (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3">
                  <div className="text-sm font-semibold text-slate-900">Seed {PRODUCT_LABELS[selected]}</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-600">Opening quantity (litres)</label>
                      <input value={qty} onChange={(e) => setQty(e.target.value)}
                             data-testid="fifo-wizard-quantity"
                             type="number" step="0.01" min="0"
                             className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5 tabular-nums" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600">Opening unit cost ($/L)</label>
                      <input value={cost} onChange={(e) => setCost(e.target.value)}
                             data-testid="fifo-wizard-cost"
                             type="number" step="0.0001" min="0"
                             className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5 tabular-nums" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-slate-600">Notes (optional)</label>
                    <input value={notes} onChange={(e) => setNotes(e.target.value)}
                           data-testid="fifo-wizard-notes"
                           placeholder="e.g. Physical count 2026-02-04, avg cost across last quarter"
                           className="mt-1 w-full text-sm rounded border border-slate-300 px-2 py-1.5" />
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200">
          <button onClick={onClose} data-testid="fifo-wizard-close-btn"
                  className="text-sm px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50">
            Close
          </button>
          {state && !state.all_seeded && selected ? (
            <button onClick={submit} disabled={saving}
                    data-testid="fifo-wizard-submit"
                    className="text-sm px-3 py-1.5 rounded bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
              {saving ? "Seeding…" : `Seed ${PRODUCT_LABELS[selected]}`}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------
   Root Workspace
   ------------------------------------------------------------------ */
const TABS = [
  { id: "snapshot",    label: "Cost Snapshot", icon: TrendUp },
  { id: "layers",      label: "Layers",        icon: Stack },
  { id: "receipts",    label: "Receipts",      icon: Truck },
  { id: "consumption", label: "Consumption",   icon: Drop },
];

export default function FifoIntelligence() {
  const [tab, setTab] = useState("snapshot");
  const [refreshKey, setRefreshKey] = useState(0);
  const [wizardOpen, setWizardOpen] = useState(false);
  const { user } = useAuth();
  const canWrite = user?.role === "Administrator" || user?.role === "Owner";

  const refresh = () => setRefreshKey((k) => k + 1);

  return (
    <div className="p-6 space-y-6" data-testid="fifo-workspace">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">FIFO Intelligence</div>
          <h1 className="text-2xl font-semibold text-slate-900">Inventory Cost Basis</h1>
          <p className="text-sm text-slate-600 max-w-2xl mt-1">
            <b>What does our inventory actually cost?</b> FIFO layers, weighted-average landed cost,
            and the true cost of the next litre out the door.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canWrite ? (
            <button type="button" onClick={() => setWizardOpen(true)}
                    data-testid="fifo-open-wizard-btn"
                    className="text-xs font-semibold px-3 py-1.5 rounded border border-slate-300 text-slate-800 hover:bg-slate-50 inline-flex items-center gap-1">
              <Sparkle size={12} weight="fill" /> Opening Wizard
            </button>
          ) : null}
          <button type="button" onClick={refresh}
                  data-testid="fifo-refresh"
                  className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 font-medium">
            <ArrowsClockwise size={12} /> Refresh
          </button>
        </div>
      </header>

      <nav className="flex flex-wrap gap-1 border-b border-slate-200" data-testid="fifo-tabs">
        {TABS.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} data-testid={`fifo-tab-${t.id}`}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-md border-b-2 ${
                    tab === t.id ? "border-slate-900 text-slate-900 font-semibold" : "border-transparent text-slate-600 hover:text-slate-900"
                  }`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </nav>

      <section>
        {tab === "snapshot"    ? <SnapshotTab refreshKey={refreshKey} canWrite={canWrite} onOpenWizard={() => setWizardOpen(true)} /> :
         tab === "layers"      ? <LayersTab refreshKey={refreshKey} /> :
         tab === "receipts"    ? <ReceiptsTab refreshKey={refreshKey} canWrite={canWrite} onRefresh={refresh} /> :
         <ConsumptionTab refreshKey={refreshKey} canWrite={canWrite} onRefresh={refresh} />}
      </section>

      <OpeningInventoryWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onDone={refresh}
      />
    </div>
  );
}
